/**
 * 身分列（`IdentityTable` 的資料層）：把**憑證**（`GET /api/accounts`，`PoolAccount`）
 * 與**在線 session**（`GET /api/sessions`，`Account`）join 成同一列。
 *
 * 為什麼要這支：`PoolAccount` 有 `hasToken`／`held`／`heldBySession`，`Account` 有
 * `online`／`room`，但畫面上沒有任何一處把「這顆憑證 → 有沒有開 session → 在不在線」
 * 放在同一列（plan-account-merge.md §2 痛點 5）。三個入口各自讀一支 API，於是同一顆
 * 帳號出現三套「可用」。
 *
 * 硬規則（純函式、不 import React、不 mutate 輸入）：
 * - join key 是 `${service}:${username}`；session 側的 username 取 `account ?? nickname`
 *   （同 `InboxLayout` 的既成慣例：`account` 是歸因帳號，`nickname` 才是登入名）。
 * - **缺欄位一律 `null`，不補 `false`／`0`**：`undefined`（後端沒回報）與 `false`（後端說沒有）
 *   是不同的事，合成一個值就等於自己造第二套定義（`types.ts:214-219`）。
 * - `state` 只有四態，判準集中在這裡，UI 不准再自己算。
 */

import type { Account, PoolAccount, Service } from './types.js';

/**
 * session 側的輸入。`Account` 沒有 `lastActivityAt`（那是 `Conversation` 才有的），
 * 但呼叫端通常直接丟 `c.conversations` 進來，所以這裡用結構型別收它，不強迫轉型。
 */
export interface IdentitySession extends Account {
  /**
   * 歸因帳號（`GET /api/sessions` 有回報才有）。型別上在 `Conversation`，
   * 但 join 需要它，所以在這裡收進來；沒有就退回 `nickname`。
   */
  account?: string | null;
  /** 最後活動時間；後端沒回報就是 `undefined`（→ 這一列顯示 `—`）。 */
  lastActivityAt?: number | null;
  /** 未讀數（`Conversation` 有）；只帶出來，不參與 join。 */
  unread?: number;
}

/**
 * 一列的狀態：
 * - `no-cred`：池裡沒有可用憑證（`hasToken === false`）→ 要重新登入／註冊
 * - `cred-broken`：有憑證但註冊/登入失敗（`ok === false`）
 * - `live`：`held === true` **且**對到 session（＝這顆正在回話）
 * - `idle`：其餘（有憑證、沒在用；或只有 session 沒有池資料）
 */
export type IdentityState = 'no-cred' | 'cred-broken' | 'idle' | 'live';

/** 一顆身分（＝一列）。`id` 是 join key，可直接當 React key 與 `data-testid` 後綴。 */
export interface IdentityRow {
  /** `${service}:${username}` */
  id: string;
  service: Service;
  username: string;
  /** 使用者取的顯示名；池沒回報或沒有就是 `null`（畫面自己退回縮寫帳號）。 */
  displayName: string | null;
  /** 有沒有憑證。`null` = 後端沒回報（`undefined`），不是「沒有」。 */
  hasCred: boolean | null;
  /** 有沒有 session 正持有憑證。`null` = 沒回報。 */
  held: boolean | null;
  /** 對到的 session 在不在線。沒有 session = `null`（不畫成離線，因為根本沒開）。 */
  online: boolean | null;
  /** 房別代號；沒進房／沒有 session = `null`。 */
  room: string | null;
  /** 對到的 session id（同一身分多條時取代表那一條）。 */
  sessionId: string | null;
  /** 同一身分開了幾條 session（`0` = 沒開，這是計數不是「有沒有」）。 */
  sessionCount: number;
  /** session 的最後活動；沒有／沒回報 = `null`。 */
  lastActivityAt: number | null;
  /** 池紀錄的最後使用；沒有／沒回報 = `null`。 */
  lastUsedAt: number | null;
  state: IdentityState;
}

/** join key。兩邊都用它，改這裡就同時改兩邊。 */
export function identityKey(service: Service, username: string): string {
  return `${service}:${username}`;
}

/** session 側的登入名：`account`（歸因帳號）優先，退回 `nickname`。 */
function sessionUsername(session: IdentitySession): string {
  const raw = session.username ?? session.account ?? session.nickname;
  return typeof raw === 'string' ? raw : '';
}

/**
 * 多條 session 同一個身分時，挑「代表那一條」。
 *
 * 排序鍵（全 deterministic，不靠輸入順序以外的隨機）：
 * 1. `heldBySession` 指向的那條（憑證真的被它持有）
 * 2. `held === true` 的身分 → 取最後活動最新
 * 3. 最後活動最新者；`null` 視為最舊
 * 4. 最後用 `id` 字串比大小，保證同樣輸入永遠同樣輸出
 */
function pickRepresentative(
  candidates: readonly IdentitySession[],
  heldBySession: string | null,
): IdentitySession | null {
  if (candidates.length === 0) return null;
  if (heldBySession !== null) {
    const match = candidates.find((item) => item.id === heldBySession);
    if (match !== undefined) return match;
  }
  const sorted = [...candidates].sort((a, b) => {
    const aTs = a.lastActivityAt ?? Number.NEGATIVE_INFINITY;
    const bTs = b.lastActivityAt ?? Number.NEGATIVE_INFINITY;
    if (aTs !== bTs) return bTs - aTs;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  return sorted[0] ?? null;
}

/**
 * 四態判準（順序有意義，先判斷的贏）：
 * 1. `hasToken === false` → `no-cred`（連憑證都沒有，「登入失敗」沒有意義）
 * 2. `ok === false` → `cred-broken`
 * 3. `held === true` 且對到 session → `live`
 * 4. 其餘 → `idle`
 *
 * 注意：只有 session、沒有池資料（`pool === null`）也是 `idle`——沒有 `held` 旗標就
 * **不猜**它是不是在回話（plan T2：`live` 需 `held===true`）。
 */
function stateOf(pool: PoolAccount | null, hasSession: boolean): IdentityState {
  if (pool !== null && pool.hasToken === false) return 'no-cred';
  if (pool !== null && pool.ok === false) return 'cred-broken';
  if (pool !== null && pool.held === true && hasSession) return 'live';
  return 'idle';
}

/** `undefined` → `null`；明確的 `null` 也 → `null`。只做這一件事，不補 0／false。 */
function nullish(value: boolean | null | undefined): boolean | null {
  return value ?? null;
}

/**
 * join 憑證列與在線 session 成身分列。
 *
 * - 輸入順序決定輸出順序：先池的順序，再接「只有 session、池裡沒有」的那些（按 `id` 排序，
 *   避免每次 render 順序跳動）。
 * - 不 mutate 輸入（不改 `pool`／`sessions` 的任何欄位，也不排序傳進來的陣列本身）。
 * - 兩邊都空 → `[]`。
 */
export function mergeIdentityRows(
  pool: readonly PoolAccount[],
  sessions: readonly IdentitySession[],
): IdentityRow[] {
  const sessionIndex = new Map<string, IdentitySession[]>();
  for (const session of sessions) {
    const key = identityKey(session.service, sessionUsername(session));
    const bucket = sessionIndex.get(key);
    if (bucket === undefined) sessionIndex.set(key, [session]);
    else bucket.push(session);
  }

  const rows: IdentityRow[] = [];
  const seen = new Set<string>();

  for (const account of pool) {
    const key = identityKey(account.service, account.username);
    // 池理論上同一 key 只會有一顆；重複時留第一顆（後端排序＝使用者看到的順序），不要長出兩列。
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(buildRow(key, account, sessionIndex.get(key) ?? null));
  }

  const orphanKeys = [...sessionIndex.keys()]
    .filter((key) => !seen.has(key))
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  for (const key of orphanKeys) {
    const bucket = sessionIndex.get(key);
    if (bucket === undefined) continue;
    const first = bucket[0];
    if (first === undefined) continue;
    rows.push(buildRow(key, null, bucket, { service: first.service, username: sessionUsername(first) }));
  }

  return rows;
}

/** 一列怎麼長出來：池有值就用池的，沒有就從 session 借 service／username。 */
function buildRow(
  key: string,
  pool: PoolAccount | null,
  sessions: readonly IdentitySession[] | null,
  orphan?: { service: Service; username: string },
): IdentityRow {
  const list = sessions ?? [];
  const hasSession = list.length > 0;
  const heldBySession = pool?.heldBySession ?? null;
  const representative = pickRepresentative(list, heldBySession);

  const service = pool?.service ?? orphan?.service ?? representative?.service;
  const username = pool?.username ?? orphan?.username ?? '';
  if (service === undefined) {
    // 理論上不可達（orphan 一定帶 service）；讓它爆掉好過靜靜回一列 `undefined`。
    throw new Error(`mergeIdentityRows: ${key} 兩邊都沒有 service`);
  }

  const displayName = pool?.displayName ?? representative?.displayName ?? null;

  return {
    id: key,
    service,
    username,
    displayName: typeof displayName === 'string' && displayName.trim() !== '' ? displayName : null,
    hasCred: nullish(pool?.hasToken),
    held: nullish(pool?.held),
    online: hasSession ? representative?.online ?? null : null,
    room: representative?.room ?? null,
    sessionId: representative?.id ?? null,
    sessionCount: list.length,
    lastActivityAt: representative?.lastActivityAt ?? null,
    lastUsedAt: pool?.lastUsedAt ?? null,
    state: stateOf(pool, hasSession),
  };
}
